import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert
} from '@mui/material';
import {
  Router as RouterIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  NetworkCheck as NetworkCheckIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { OLT, ConnectionResult } from '../../../services/oltService';
import MonitorInfoCard from '../../monitoramento/components/MonitorInfoCard';

interface DashboardTabProps {
  olt: OLT;
  connectionTest: {
    testing: boolean;
    result: ConnectionResult | null;
  };
  onTestConnection: () => void;
  onRefresh: () => void;
}

const DashboardTab: React.FC<DashboardTabProps> = ({
  olt,
  connectionTest,
  onTestConnection,
  onRefresh
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'offline':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'maintenance':
        return <PauseIcon color="warning" fontSize="small" />;
      default:
        return <SettingsIcon fontSize="small" />;
    }
  };

  return (
    <Box sx={{ p: 1.5 }}>
    <Grid container spacing={1.5}>
      {/* Card Principal */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontSize: '1.25rem', mb: 0.25 }}>
                  {olt.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  {olt.vendor.toUpperCase()} - {olt.model}
                </Typography>
              </Box>
              <Chip
                icon={getStatusIcon(olt.status)}
                label={olt.status}
                color={getStatusColor(olt.status) as any}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  IP de Gerenciamento
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  {olt.ip_address}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Porta SNMP
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  {olt.snmp_port || '161'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Comunidade SNMP
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ fontSize: '0.8rem' }}>
                  {olt.snmp_community || 'public'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Status
                </Typography>
                <Chip
                  label={olt.ativo ? 'Ativa' : 'Inativa'}
                  color={olt.ativo ? 'success' : 'error'}
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<NetworkCheckIcon />}
                onClick={onTestConnection}
                disabled={connectionTest.testing}
                sx={{ mr: 1, fontSize: '0.8rem' }}
                size="small"
              >
                {connectionTest.testing ? 'Testando...' : 'Testar Conexão'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                size="small"
                sx={{ fontSize: '0.8rem' }}
              >
                Atualizar
              </Button>
            </Box>

            {connectionTest.result && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={connectionTest.result.success ? 'success' : 'error'}>
                  {connectionTest.result.message}
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Card de Estatísticas */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
              <StorageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Estatísticas
            </Typography>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <RouterIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Portas PON"
                  secondary={olt.pon_ports?.length || 0}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SpeedIcon color="secondary" />
                </ListItemIcon>
                <ListItemText
                  primary="ONUs Ativas"
                  secondary={olt.pon_ports?.reduce((total, port) => total + (port.current_onus || 0), 0) || 0}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LocationIcon color="info" />
                </ListItemIcon>
                <ListItemText
                  primary="Capacidade Total"
                  secondary={olt.pon_ports?.reduce((total, port) => total + (port.max_onus || 0), 0) || 0}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Card de Monitoramento */}
        <Box sx={{ mt: 1.5 }}>
          <MonitorInfoCard
            status={olt.status}
            timestamps={{
              ultimoOnlineEm: olt.monitor_ultimo_online_em,
              ultimoOfflineEm: olt.monitor_ultimo_offline_em,
            }}
          />
        </Box>

        {/* Card de Informações Adicionais */}
        <Card sx={{ mt: 1.5 }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
              <InfoIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Informações
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="Fabricante"
                  secondary={olt.vendor}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Modelo"
                  secondary={olt.model}
                />
              </ListItem>
              {olt.description && (
                <ListItem>
                  <ListItemText
                    primary="Descrição"
                    secondary={olt.description}
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    </Box>
  );
};

export default DashboardTab; 